import React, { useState } from 'react';
import { Globe, Lock, Plus, Trash2, X, BookOpen } from 'lucide-react';
import { getTcgBadgeColor } from './CollectionList';
import ShareButton from './ShareButton';
import CollectionCardModal from './CollectionCardModal';

export default function CollectionDetail({
  collection, user, lang, t,
  onTogglePublic, onAddCard, onDeleteCollection, onRemoveCard
}) {
  const [selectedCard, setSelectedCard] = useState(null);

  if (!collection) {
    return (
      <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-12 text-center text-[#A8B5AE]">
        <BookOpen size={48} className="mx-auto mb-4 opacity-30" />
        <p>{t('collectionsShared.select_collection')}</p>
      </div>
    );
  }

  const isOwner = user && collection.user_id === user.user_id;

  return (
    <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>
              {collection.name}
            </h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getTcgBadgeColor(collection.tcg_type)}`}>
              {collection.tcg_type}
            </span>
          </div>
          <p className="text-[#A8B5AE] text-sm">{collection.description}</p>
          {collection.user_name && (
            <p className="text-[#C9A962] text-xs mt-2">
              {t('common.by')} @{collection.user_name}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          {isOwner && (
            <>
              <button
                onClick={() => onTogglePublic(collection)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  collection.is_public
                    ? 'border-[#2D8B70]/30 text-[#2D8B70] bg-[#2D8B70]/10'
                    : 'border-[#C9A962]/20 text-[#A8B5AE] bg-[#0A1612]'
                }`}
                data-testid="toggle-public-btn"
              >
                {collection.is_public ? (
                  <><Globe size={12} className="inline mr-1" />{t('collections.public')}</>
                ) : (
                  <><Lock size={12} className="inline mr-1" />{t('collections.private')}</>
                )}
              </button>
              <button
                onClick={onAddCard}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#C9A962]/20 text-[#C9A962] border border-[#C9A962]/20 hover:bg-[#C9A962]/30 transition-all"
                data-testid="add-card-btn"
              >
                <Plus size={12} className="inline mr-1" />{t('collections.add_card')}
              </button>
              <button
                onClick={() => onDeleteCollection(collection.collection_id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#C45B4A]/10 text-[#F87171] border border-[#C45B4A]/20 hover:bg-[#C45B4A]/20 transition-all"
                data-testid="delete-collection-btn"
              >
                <Trash2 size={12} />
              </button>
            </>
          )}
          {collection.is_public && (
            <ShareButton collectionId={collection.collection_id} lang={lang} t={t} />
          )}
        </div>
      </div>

      {/* Cards Grid */}
      {collection.cards?.length === 0 ? (
        <div className="text-center py-12 text-[#A8B5AE]">
          <p className="text-sm">{t('collectionsShared.no_cards_in_collection')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {collection.cards?.map((card, i) => (
            <div key={`${card.external_id}-${i}`} className="relative group" data-testid={`card-${card.external_id}`}>
              <div
                className="aspect-[2/3] rounded-xl overflow-hidden bg-[#162B24] cursor-pointer hover:ring-2 hover:ring-[#C9A962]/50 transition-all"
                onClick={() => setSelectedCard(card)}
              >
                <img
                  src={card.image_url || 'https://via.placeholder.com/150x210'}
                  alt={card.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>
              <p
                className="text-[#F5F0E6] text-xs font-semibold mt-1 truncate cursor-pointer hover:text-[#C9A962] transition-colors"
                onClick={() => setSelectedCard(card)}
              >
                {card.name}
              </p>
              {card.quantity > 1 && (
                <span className="absolute top-1 right-1 bg-[#C9A962] text-[#0A1612] text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold pointer-events-none">
                  ×{card.quantity}
                </span>
              )}
              {isOwner && (
                <button
                  onClick={e => { e.stopPropagation(); onRemoveCard(card.external_id); }}
                  className="absolute top-1 left-1 w-6 h-6 bg-[#C45B4A]/80 rounded-full items-center justify-center hidden group-hover:flex transition-all z-10"
                  data-testid={`remove-card-${card.external_id}`}
                >
                  <X size={12} className="text-white" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CollectionCardModal
          card={selectedCard}
          lang={lang}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </div>
  );
}
